<div class="w-full mx-auto h-screen p-5">
  <!-- Header -->
  <div class="bg-white p-6 rounded-lg shadow-sm mb-6">
    <h1 class="text-2xl font-bold text-gray-800">Nueva Inscripción</h1>
    <p class="text-gray-500 mt-1">Seleccione el tipo de inscripción y complete la información requerida</p>
  </div>

  <!-- ⚠️ Bloqueo de inscripción -->
  <div *ngIf="isBlocked" class="bg-red-500 text-white p-4 rounded-md text-center mb-6">
    ⚠️ Alguien más está inscribiendo a este estudiante en este proyecto. Espere...
  </div>

  <!-- Selector de Tipo de Inscripción -->
  <div *ngIf="!isBlocked && enrollmentForm" class="bg-white p-6 rounded-lg shadow-sm mb-6" [formGroup]="enrollmentForm">
    <h2 class="text-lg font-semibold text-gray-800 mb-4">Tipo de Inscripción</h2>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div *ngFor="let type of enrollmentTypes" class="border-2 rounded-lg p-4 cursor-pointer transition-all" [ngClass]="{
             'border-blue-500 bg-blue-50': enrollmentForm.get('enrollmentType')?.value === type.value,
             'border-gray-200 hover:border-gray-300': enrollmentForm.get('enrollmentType')?.value !== type.value
           }" (click)="enrollmentForm.get('enrollmentType')?.setValue(type.value)">

        <div class="flex items-center mb-2">
          <input type="radio" [value]="type.value" formControlName="enrollmentType"
            class="text-blue-600 focus:ring-blue-500">
          <label class="ml-2 text-sm font-medium text-gray-900">
            {{ type.icon }} {{ type.label }}
          </label>
        </div>
        <p class="text-xs text-gray-500">{{ type.description }}</p>
      </div>
    </div>

    <div *ngIf="enrollmentForm.get('enrollmentType')?.invalid && enrollmentForm.get('enrollmentType')?.touched"
      class="text-red-500 text-sm mt-2">
      Debe seleccionar un tipo de inscripción
    </div>
  </div>

  <!-- 🏢 Flujo Empresarial Mejorado (Nacional e Internacional) -->
  <div
    *ngIf="!isBlocked && enrollmentForm && (selectedEnrollmentType==='business' || selectedEnrollmentType==='business-international')">

    <!-- 🌍 Banner informativo para inscripciones corporativas internacionales -->
    <div *ngIf="businessIsInternational" class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-lg">
      <div class="flex">
        <div class="flex-shrink-0">
          <span class="text-2xl">🌐</span>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-blue-800">Inscripción Corporativa Internacional</h3>
          <div class="mt-2 text-sm text-blue-700">
            <p>Los montos se registrarán en <strong>{{ businessCurrencyCode }}</strong>.</p>
            <p *ngIf="businessExchangeRate > 0" class="mt-1">
              Tasa de cambio actual: <strong>{{ formatBusinessExchangeRate() }}</strong>
              <span *ngIf="businessLastExchangeRateUpdate" class="text-xs text-blue-600 ml-2">
                (Actualizada: {{ businessLastExchangeRateUpdate | date:'dd/MM/yyyy HH:mm' }})
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Barra de pasos para flujo empresarial -->
    <div class="flex items-center justify-center mb-8">
      <div class="flex w-full max-w-5xl">
        <ng-container
          *ngFor="let label of ['Datos Básicos', 'Modalidad', 'Participantes', 'Planes de Pago', 'Resumen']; let i = index">
          <div class="flex flex-1 text-center items-center">
            <div class="flex items-center justify-center w-10 h-10 rounded-full" [ngClass]="{
                'bg-blue-500 text-white': businessStep === i + 1,
                'bg-green-500 text-white': businessStep > i + 1,
                'bg-gray-300 text-gray-600': businessStep < i + 1
              }">
              <span *ngIf="businessStep <= i + 1">{{ i + 1 }}</span>
              <svg *ngIf="businessStep > i + 1" class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="mx-2 flex-1">
              <span class="text-sm font-medium">{{ label }}</span>
            </div>
            <div *ngIf="i < 4" class="flex-1 h-0.5 bg-gray-300" [ngClass]="{'bg-blue-500': businessStep > i + 1}"></div>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- STEP 1: Datos Básicos de Inscripción Empresarial -->
    <div *ngIf="businessStep === 1" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">
          Datos Básicos - Inscripción Empresarial
          <span *ngIf="businessIsInternational" class="text-blue-600 ml-2">🌐 Internacional</span>
        </h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Empresa -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>🏢 Empresa</label>
          <p-dropdown [filter]="true" filterBy="label" [showClear]="true" [options]="companies"
            placeholder="Seleccione una empresa" [(ngModel)]="selectedCompanyId" [ngModelOptions]="{standalone: true}"
            (onFilter)="onSearchCompanies($event)" class="w-full custom-dropdown">
          </p-dropdown>
          <p class="text-xs text-gray-500 mt-1">Empresa que pagará las inscripciones de los colaboradores</p>
        </div>

        <!-- Proyecto -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>📁 Proyecto</label>
          <p-dropdown [filter]="true" filterBy="label" [showClear]="true" [options]="projects"
            placeholder="Seleccione un proyecto" [(ngModel)]="selectedProjectId" [ngModelOptions]="{standalone: true}"
            (onFilter)="onSearchProjects($event)" class="w-full custom-dropdown">
          </p-dropdown>
          <p class="text-xs text-gray-500 mt-1">Proyecto en el que se inscribirán los colaboradores</p>
        </div>

        <!-- 🆕 Monto editable de la inscripción -->
        <div class="md:col-span-2">
          <label class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>💰 Monto de Inscripción del
            Programa</label>
          <div class="relative">
            <input type="text" appCurrencyFormatter [(ngModel)]="businessInscriptionAmount"
              [ngModelOptions]="{standalone: true}" (ngModelChange)="calculateConsolidatedTotals()" min="0" step="1000"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ej: 3000000">
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span class="text-gray-400 text-sm">{{ businessCurrencyCode }}</span>
            </div>
          </div>
          <div class="flex items-center justify-between mt-2">
            <p class="text-xs text-gray-600">
              🔧 Costo base por colaborador. Puede editar este valor según las necesidades del acuerdo corporativo.
            </p>
            <button type="button" (click)="initializeInscriptionAmount()"
              class="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-full transition-colors">
              Usar valor del proyecto
            </button>
          </div>
          <div *ngIf="selectedProject" class="text-xs text-gray-500 mt-1">
            💡 Valor sugerido del proyecto: {{ getBusinessCurrencySymbol() }}{{ getProjectInscriptionAmount() }}
          </div>
          <!-- 🌍 Equivalente en COP para inscripciones internacionales -->
          <div *ngIf="businessIsInternational && businessExchangeRate > 0"
            class="text-xs text-blue-600 mt-1 bg-blue-50 p-2 rounded">
            💱 Equivalente en COP: {{ calculateBusinessCOPEquivalent(businessInscriptionAmount) | number:'1.0-0' }} COP
          </div>
        </div>

        <!-- Observaciones -->
        <div class="md:col-span-2">
          <label class="block text-sm font-semibold text-gray-700 mb-2">📝 Observaciones</label>
          <textarea [(ngModel)]="businessObservations" [ngModelOptions]="{standalone: true}"
            class="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows="3" placeholder="Observaciones adicionales sobre el acuerdo empresarial (opcional)"></textarea>
        </div>

        <!-- Estado Inicial -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>Estado Inicial</label>
          <p-dropdown [options]="statusOptions" [(ngModel)]="businessStatus" [ngModelOptions]="{standalone: true}"
            optionLabel="label" optionValue="value" class="w-full custom-dropdown" placeholder="Seleccione el estado">
          </p-dropdown>
        </div>

        <!-- 🆕 Cobro automático vía email -->
        <div class="md:col-span-2">
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <label class="block text-sm font-semibold text-gray-700 mb-1">📧 Cobro automático vía email</label>
                <p class="text-xs text-gray-600">
                  Si está activo, el sistema generará links de pago automáticamente y los enviará por email 2 días antes
                  del vencimiento de cada cuota.
                </p>
              </div>
              <div class="ml-4">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" [(ngModel)]="businessAutoGeneratePaymentLinks"
                    [ngModelOptions]="{standalone: true}" class="sr-only peer">
                  <div
                    class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600">
                  </div>
                </label>
              </div>
            </div>
            <div class="mt-2 text-xs" [ngClass]="{
              'text-green-600': businessAutoGeneratePaymentLinks,
              'text-orange-600': !businessAutoGeneratePaymentLinks
            }">
              <span *ngIf="businessAutoGeneratePaymentLinks">
                ✅ Los participantes recibirán links de pago automáticamente por email.
              </span>
              <span *ngIf="!businessAutoGeneratePaymentLinks">
                ⚠️ No se generarán links automáticos. Los pagos se gestionarán manualmente.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 2: Modalidad de Cobertura 🆕 -->
    <div *ngIf="businessStep === 2" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">Modalidad de Cobertura</h3>
      </div>

      <!-- Información del proyecto y empresa seleccionados -->
      <div *ngIf="selectedProject && getSelectedCompanyName()"
        class="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200 mb-6">
        <div class="flex items-center mb-4">
          <span class="text-blue-600 font-semibold text-lg">📋 Información del Acuerdo</span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-gray-600 font-medium">Empresa:</span>
              <span class="font-semibold text-gray-800">{{ getSelectedCompanyName() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 font-medium">Proyecto:</span>
              <span class="font-semibold text-gray-800">{{ selectedProject?.name }}</span>
            </div>
          </div>
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-gray-600 font-medium">Costo por participante:</span>
              <span class="font-semibold text-green-600 text-lg">{{ getBusinessCurrencySymbol() }}{{
                businessInscriptionAmount | number:'1.0-0' }}</span>
            </div>
            <div *ngIf="businessIsInternational && businessExchangeRate > 0" class="flex justify-between text-sm">
              <span class="text-gray-500">Equivalente en COP:</span>
              <span class="text-blue-600">{{ calculateBusinessCOPEquivalent(businessInscriptionAmount) | number:'1.0-0'
                }} COP</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 font-medium">Modalidad:</span>
              <span class="font-semibold text-gray-800">{{ selectedCoverageModality === CoverageModality.Total ? 'Total
                (100%)' : 'Parcial' }}</span>
            </div>
            <div *ngIf="businessIsInternational" class="flex justify-between">
              <span class="text-gray-600 font-medium">Moneda:</span>
              <span class="font-semibold text-blue-600">🌐 {{ businessCurrencyCode }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Selección de modalidades -->
      <div class="mb-8">
        <h4 class="text-lg font-semibold text-gray-800 mb-4">Seleccione la Modalidad de Cobertura</h4>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Modalidad A: Cobertura Total -->
          <div class="border-2 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg" [ngClass]="{
                 'border-green-500 bg-green-50 shadow-lg': selectedCoverageModality === CoverageModality.Total,
                 'border-gray-200 hover:border-gray-300': selectedCoverageModality !== CoverageModality.Total
               }" (click)="onCoverageModalityChange(CoverageModality.Total)">

            <div class="flex items-center mb-4">
              <input type="radio" [value]="CoverageModality.Total"
                [checked]="selectedCoverageModality === CoverageModality.Total"
                (change)="onCoverageModalityChange(CoverageModality.Total)"
                class="text-green-600 focus:ring-green-500 mr-3">
              <div class="text-2xl mr-3">🟢</div>
              <div>
                <h5 class="text-lg font-bold text-gray-800">Modalidad A: Cobertura Total</h5>
                <span class="text-sm text-green-600 font-medium">La empresa paga el 100%</span>
              </div>
            </div>

            <div class="space-y-3 text-sm text-gray-700">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>1 solo conjunto de links de pago para la empresa</span>
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Los colaboradores no pagan nada</span>
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Gestión de pagos simplificada</span>
              </div>
            </div>

            <div class="mt-4 p-3 bg-green-100 rounded-lg">
              <span class="text-green-800 font-medium text-sm">
                💼 Ideal para empresas que quieren cubrir completamente la formación de sus colaboradores
              </span>
            </div>
          </div>

          <!-- Modalidad B: Cobertura Parcial -->
          <div class="border-2 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg" [ngClass]="{
                 'border-yellow-500 bg-yellow-50 shadow-lg': selectedCoverageModality === CoverageModality.Partial,
                 'border-gray-200 hover:border-gray-300': selectedCoverageModality !== CoverageModality.Partial
               }" (click)="onCoverageModalityChange(CoverageModality.Partial)">

            <div class="flex items-center mb-4">
              <input type="radio" [value]="CoverageModality.Partial"
                [checked]="selectedCoverageModality === CoverageModality.Partial"
                (change)="onCoverageModalityChange(CoverageModality.Partial)"
                class="text-yellow-600 focus:ring-yellow-500 mr-3">
              <div class="text-2xl mr-3">🟡</div>
              <div>
                <h5 class="text-lg font-bold text-gray-800">Modalidad B: Cobertura Parcial</h5>
                <span class="text-sm text-yellow-600 font-medium">Empresa + participantes</span>
              </div>
            </div>

            <div class="space-y-3 text-sm text-gray-700">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Links separados para empresa y cada participante</span>
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Cobertura personalizable por participante</span>
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"></path>
                </svg>
                <span>Refinanciación individual disponible</span>
              </div>
            </div>

            <div class="mt-4 p-3 bg-yellow-100 rounded-lg">
              <span class="text-yellow-800 font-medium text-sm">
                🤝 Ideal para acuerdos de co-inversión en formación profesional
              </span>
            </div>
          </div>
        </div>

        <!-- Descripción de modalidad seleccionada -->
        <div class="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p class="text-blue-800 font-medium">{{ getCoverageModalityDescription() }}</p>
        </div>
      </div>
    </div>

    <!-- STEP 3: Participantes y Cobertura -->
    <div *ngIf="businessStep === 3" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">Participantes y Cobertura</h3>
      </div>

      <!-- 🆕 Dashboard consolidado de cálculos -->
      <div *ngIf="selectedProject"
        class="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-200 mb-8">
        <div class="flex items-center justify-between mb-6">
          <h4 class="text-xl font-bold text-gray-800">📊 Resumen de Inscripción Corporativa</h4>
          <span class="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
            {{ selectedCoverageModality === CoverageModality.Total ? 'Modalidad A: Total' : 'Modalidad B: Parcial' }}
          </span>
        </div>

        <!-- Información base del proyecto -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="flex items-center mb-2">
              <span class="text-2xl mr-2">🏢</span>
              <span class="text-sm font-medium text-gray-600">Empresa</span>
            </div>
            <p class="text-lg font-bold text-gray-800">{{ getSelectedCompanyName() }}</p>
          </div>

          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="flex items-center mb-2">
              <span class="text-2xl mr-2">📁</span>
              <span class="text-sm font-medium text-gray-600">Proyecto</span>
            </div>
            <p class="text-lg font-bold text-gray-800">{{ selectedProject?.name }}</p>
          </div>

          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <div class="flex items-center mb-2">
              <span class="text-2xl mr-2">💰</span>
              <span class="text-sm font-medium text-gray-600">Costo Base</span>
            </div>
            <p class="text-lg font-bold text-green-600">{{ formatCurrency(businessInscriptionAmount) }}</p>
            <p class="text-xs text-gray-500">Por participante</p>
          </div>
        </div>

        <!-- 🆕 Cálculos consolidados detallados -->
        <div class="bg-white p-6 rounded-lg border-2 border-indigo-200 shadow-md">
          <h5 class="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <span class="text-2xl mr-2">📈</span>
            Cálculos Consolidados
          </h5>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <!-- Total participantes -->
            <div class="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div class="text-3xl font-bold text-blue-600">{{ businessParticipants.length }}</div>
              <div class="text-sm text-blue-700 font-medium">Participantes</div>
            </div>

            <!-- Total inscripción -->
            <div class="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div class="text-xl font-bold text-purple-600">{{ formatCurrency(totalEnrollmentAmount) }}</div>
              <div class="text-sm text-purple-700 font-medium">Total Inscripción</div>
              <div class="text-xs text-purple-600 mt-1">{{ businessParticipants.length }} × {{
                formatCurrency(businessInscriptionAmount) }}</div>
            </div>

            <!-- Empresa paga -->
            <div class="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div class="text-xl font-bold text-green-600">{{ formatCurrency(totalCompanyAmount) }}</div>
              <div class="text-sm text-green-700 font-medium">Empresa Paga</div>
              <div class="text-xs text-green-600 mt-1">
                {{ selectedCoverageModality === CoverageModality.Total ? '100%' :
                ((totalCompanyAmount / totalEnrollmentAmount) * 100).toFixed(1) + '%' }}
              </div>
            </div>

            <!-- Participantes pagan -->
            <div class="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div class="text-xl font-bold text-orange-600">{{ formatCurrency(totalParticipantAmount) }}</div>
              <div class="text-sm text-orange-700 font-medium">Participantes Pagan</div>
              <div class="text-xs text-orange-600 mt-1">
                {{ selectedCoverageModality === CoverageModality.Total ? '0%' :
                ((totalParticipantAmount / totalEnrollmentAmount) * 100).toFixed(1) + '%' }}
              </div>
            </div>
          </div>

          <!-- Desglose detallado si hay participantes -->
          <div *ngIf="businessParticipants.length > 0" class="mt-6 p-4 bg-gray-50 rounded-lg">
            <h6 class="font-semibold text-gray-800 mb-3">💡 Desglose por Participante:</h6>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-gray-600">Promedio empresa por participante:</span>
                <span class="font-bold ml-2 text-green-600">
                  {{ formatCurrency(businessParticipants.length > 0 ? totalCompanyAmount / businessParticipants.length :
                  0) }}
                </span>
              </div>
              <div>
                <span class="text-gray-600">Promedio participante individual:</span>
                <span class="font-bold ml-2 text-orange-600">
                  {{ formatCurrency(businessParticipants.length > 0 ? totalParticipantAmount /
                  businessParticipants.length : 0) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Los parámetros de plan de pagos se han movido al Step 4 -->

      <!-- Lista de Participantes -->
      <div class="mb-6">
        <div class="flex items-center justify-between mb-4">
          <h4 class="text-md font-semibold text-gray-800">👥 Colaboradores a Inscribir</h4>
          <button type="button" (click)="addBusinessParticipant()"
            class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clip-rule="evenodd"></path>
            </svg>
            Agregar Colaborador
          </button>
        </div>

        <!-- Mensaje cuando no hay participantes -->
        <div *ngIf="businessParticipants.length === 0"
          class="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-2.25M13 7a4 4 0 11-8 0 4 4 0 018 0z">
            </path>
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">Sin colaboradores</h3>
          <p class="mt-1 text-sm text-gray-500">Agregue al menos un colaborador para continuar</p>
        </div>

        <!-- Tarjetas de participantes -->
        <div *ngFor="let participant of businessParticipants; let i = index"
          class="border border-gray-200 rounded-lg p-6 mb-4 hover:shadow-md transition-shadow">

          <!-- Header de la tarjeta -->
          <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
            <div class="flex items-center">
              <div
                class="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                {{ i + 1 }}
              </div>
              <div>
                <h5 class="font-semibold text-gray-800">Colaborador #{{ i + 1 }}</h5>
                <p class="text-xs text-gray-500">Configuración de cobertura</p>
              </div>
            </div>

            <button type="button" (click)="removeBusinessParticipant(i)"
              class="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-full transition-colors">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"></path>
              </svg>
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Participante -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>👤 Participante</label>
              <p-dropdown [filter]="true" [showClear]="true" [options]="participants" resetFilterOnHide="true"
                [loading]="isSearchingParticipants" [(ngModel)]="participant.participantId"
                [ngModelOptions]="{standalone: true}" (ngModelChange)="onParticipantChange()"
                (onFilter)="onSearchParticipants($event)" placeholder="Seleccione un participante"
                class="w-full custom-dropdown">
              </p-dropdown>
            </div>

            <!-- Tipo de cobertura -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>💼 Tipo de
                Cobertura</label>
              <select [(ngModel)]="participant.coverageType" [ngModelOptions]="{standalone: true}"
                (change)="onCoverageTypeChange(participant); onParticipantChange()"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="FULL">100% Empresa (Gratis para colaborador)</option>
                <option value="PERCENTAGE">% Empresa (Resto colaborador)</option>
                <option value="FIXED">Valor fijo empresa (Resto colaborador)</option>
                <option value="NONE">0% Empresa (100% colaborador)</option>
              </select>
            </div>

            <!-- Valor de cobertura -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                💰
                <span *ngIf="participant.coverageType === 'PERCENTAGE'">Porcentaje (%)</span>
                <span *ngIf="participant.coverageType === 'FIXED'">Valor (COP)</span>
                <span *ngIf="participant.coverageType === 'FULL' || participant.coverageType === 'NONE'">-</span>
              </label>
              <input type="number" [(ngModel)]="participant.coverageValue" [ngModelOptions]="{standalone: true}"
                (ngModelChange)="onParticipantChange()"
                [disabled]="participant.coverageType === 'FULL' || participant.coverageType === 'NONE'"
                [placeholder]="getCoveragePlaceholder(participant.coverageType)" [min]="0"
                [max]="participant.coverageType === 'PERCENTAGE' ? 100 : null"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100" />
            </div>
          </div>

          <!-- Visualización de cobertura -->
          <div class="mt-4 p-3 bg-gray-50 rounded-lg">
            <div class="text-sm">
              <span class="font-medium text-gray-700">Distribución de costos:</span>
              <div class="mt-1 flex justify-between">
                <span>Empresa paga: <span class="font-semibold text-green-600">{{ getCompanyCoverage(participant)
                    }}</span></span>
                <span>Colaborador paga: <span class="font-semibold text-blue-600">{{ getParticipantCoverage(participant)
                    }}</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 🆕 STEP 4: Configuración de Planes de Pago -->
    <div *ngIf="businessStep === 4" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">Configuración de Planes de Pago</h3>
      </div>

      <!-- Información de distribución de costos -->
      <div class="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-200 mb-8">
        <h4 class="text-lg font-bold text-gray-800 mb-4 flex items-center">
          <span class="text-2xl mr-2">💰</span>
          Distribución de Costos
        </h4>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div class="text-xl font-bold text-purple-600">{{ formatCurrency(totalEnrollmentAmount) }}</div>
            <div class="text-sm text-purple-700 font-medium">Total del Programa</div>
          </div>
          <div class="text-center p-4 bg-green-50 rounded-lg border border-green-200">
            <div class="text-xl font-bold text-green-600">{{ formatCurrency(totalCompanyAmount) }}</div>
            <div class="text-sm text-green-700 font-medium">Empresa Pagará</div>
          </div>
          <div class="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div class="text-xl font-bold text-orange-600">{{ formatCurrency(totalParticipantAmount) }}</div>
            <div class="text-sm text-orange-700 font-medium">Participantes Pagarán</div>
          </div>
        </div>
      </div>

      <!-- Formularios de planes de pago -->
      <div class="grid grid-cols-1 gap-8">

        <!-- 💼 Plan de Pagos de la Empresa -->
        <div class="border border-gray-200 rounded-lg p-6 bg-gray-50">
          <div class="flex items-center justify-between mb-6">
            <h5 class="text-lg font-semibold text-gray-800 flex items-center">
              <span class="text-2xl mr-2">🏢</span>
              Plan de Pagos de la Empresa
            </h5>
            <div class="flex items-center gap-3">
              <button type="button" (click)="distributeCompanyAmount()"
                class="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded-md text-sm transition-colors">
                🧮 Distribuir automáticamente
              </button>
              <button type="button" (click)="addCompanyPaymentPlan()" [disabled]="getCompanyRemainingAmount() <= 0"
                class="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clip-rule="evenodd"></path>
                </svg>
                Agregar Cuota
              </button>
            </div>
          </div>

          <!-- 🎯 Cuadro de Control del Plan de Empresa -->
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h6 class="text-sm font-semibold text-blue-800 mb-3 flex items-center">
              <span class="mr-2">📊</span>
              Control de Distribución - Plan de Empresa
            </h6>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- Total a pagar por la empresa -->
              <div class="text-center p-3 bg-white rounded-lg border border-blue-200">
                <div class="text-lg font-bold text-blue-600">{{ formatCurrency(totalCompanyAmount) }}</div>
                <div class="text-xs text-blue-700">Total a Pagar (Empresa)</div>
              </div>

              <!-- Total asignado en cuotas -->
              <div class="text-center p-3 bg-white rounded-lg border border-blue-200">
                <div class="text-lg font-bold" [ngClass]="{
                       'text-green-600': getCompanyAssignedAmount() <= totalCompanyAmount,
                       'text-red-600': getCompanyAssignedAmount() > totalCompanyAmount
                     }">
                  {{ formatCurrency(getCompanyAssignedAmount()) }}
                </div>
                <div class="text-xs text-gray-700">Total Asignado en Cuotas</div>
              </div>

              <!-- Restante por asignar -->
              <div class="text-center p-3 bg-white rounded-lg border border-blue-200">
                <div class="text-lg font-bold" [ngClass]="{
                       'text-gray-600': getCompanyRemainingAmount() === 0,
                       'text-orange-600': getCompanyRemainingAmount() > 0,
                       'text-red-600': getCompanyRemainingAmount() < 0
                     }">
                  {{ formatCurrency(getCompanyRemainingAmount()) }}
                </div>
                <div class="text-xs text-gray-700">
                  {{ getCompanyRemainingAmount() === 0 ? 'Distribución Completa' :
                  getCompanyRemainingAmount() > 0 ? 'Restante por Asignar' :
                  'Exceso Asignado' }}
                </div>
              </div>
            </div>

            <!-- Barra de progreso -->
            <div class="mt-3">
              <div class="flex justify-between text-xs text-gray-600 mb-1">
                <span>Progreso: {{ companyPaymentPlans.length }} cuotas</span>
                <span>{{ getCompanyProgressPercentage() }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div class="h-2 rounded-full transition-all duration-300" [ngClass]="{
                       'bg-green-500': getCompanyProgressPercentage() <= 100,
                       'bg-red-500': getCompanyProgressPercentage() > 100
                     }" [style.width.%]="Math.min(getCompanyProgressPercentage(), 100)">
                </div>
              </div>
            </div>
            <p *ngIf="!isCompanyPlansBalanced()" class="text-xs font-semibold text-red-600 mt-1">
              Las cuotas de empresa ({{ getCompanyPlansSum() | currency:'COP':'symbol':'1.0-2' }})
              no cuadran con la cobertura esperada ({{ getExpectedCompanyTotal() | currency:'COP':'symbol':'1.0-2' }}).
            </p>
          </div>

          <div class="space-y-4">
            <div *ngFor="let plan of companyPaymentPlans; let i = index"
              class="bg-white border border-gray-200 rounded-lg p-4">
              <div class="flex items-center justify-between mb-3">
                <h6 class="font-semibold text-gray-800">Cuota #{{ plan.installmentNumber }} - Empresa</h6>
                <button type="button" (click)="removeCompanyPaymentPlan(i)"
                  class="text-red-600 hover:text-red-800 p-1 rounded transition-colors">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"></path>
                  </svg>
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">📅 Fecha de Vencimiento</label>
                  <p-calendar [(ngModel)]="plan.dueDate" [ngModelOptions]="{standalone: true}"
                    class="w-full tw-calendar" placeholder="Seleccionar fecha" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">💰 Monto a Pagar</label>
                  <input type="text" appCurrencyFormatter [(ngModel)]="plan.amountToPay"
                    [ngModelOptions]="{standalone: true}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0" />
                  <div class="mt-2 flex items-center">
                    <input type="checkbox" [(ngModel)]="plan.generateLink" [ngModelOptions]="{standalone: true}"
                      class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                    <label class="ml-2 text-xs text-gray-700">Generar link de pago automático</label>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">📝 Observaciones</label>
                  <input type="text" [(ngModel)]="plan.observations" [ngModelOptions]="{standalone: true}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Opcional" />
                </div>
              </div>
            </div>

            <!-- Mensaje cuando no hay cuotas -->
            <div *ngIf="companyPaymentPlans.length === 0"
              class="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <div class="text-gray-400 mb-2">
                <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1">
                  </path>
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900">Sin cuotas definidas</h3>
              <p class="text-sm text-gray-500">Agregue al menos una cuota para la empresa</p>
            </div>
          </div>
        </div>

        <!-- 👥 Plan Maestro de Participantes (solo para modalidad Parcial) -->
        <div *ngIf="selectedCoverageModality === CoverageModality.Partial"
          class="border border-gray-200 rounded-lg p-6 bg-gray-50">
          <div class="flex items-center justify-between mb-6">
            <h5 class="text-lg font-semibold text-gray-800 flex items-center">
              <span class="text-2xl mr-2">👥</span>
              Plan Maestro de Participantes
            </h5>
            <div class="flex items-center gap-3">
              <button type="button" (click)="distributeParticipantAmount()"
                class="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded-md text-sm transition-colors">
                🧮 Distribuir automáticamente
              </button>
              <button type="button" (click)="addMasterPaymentPlan()" [disabled]="getMasterRemainingAmount() <= 0"
                class="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clip-rule="evenodd"></path>
                </svg>
                Agregar Cuota
              </button>
            </div>
          </div>

          <div class="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-6">
            <p class="text-orange-800 text-sm">
              <span class="font-semibold">💡 Plan Maestro:</span> Este plan se aplicará automáticamente a cada
              participante. Cada colaborador tendrá su propio conjunto de cuotas basado en este template, permitiendo
              refinanciación individual si es necesario.
            </p>
          </div>

          <!-- 🎯 Cuadro de Control del Plan Maestro -->
          <div class="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4 mb-6">
            <h6 class="text-sm font-semibold text-orange-800 mb-3 flex items-center">
              <span class="mr-2">📊</span>
              Control de Distribución - Plan Maestro (por participante)
            </h6>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- Total a pagar por participante -->
              <div class="text-center p-3 bg-white rounded-lg border border-orange-200">
                <div class="text-lg font-bold text-orange-600">{{ formatCurrency(getParticipantTotalAmount()) }}</div>
                <div class="text-xs text-orange-700">Total por Participante</div>
              </div>

              <!-- Total asignado en cuotas del plan maestro -->
              <div class="text-center p-3 bg-white rounded-lg border border-orange-200">
                <div class="text-lg font-bold" [ngClass]="{
                       'text-green-600': getMasterAssignedAmount() <= getParticipantTotalAmount(),
                       'text-red-600': getMasterAssignedAmount() > getParticipantTotalAmount()
                     }">
                  {{ formatCurrency(getMasterAssignedAmount()) }}
                </div>
                <div class="text-xs text-gray-700">Total Asignado en Plan</div>
              </div>

              <!-- Restante por asignar del plan maestro -->
              <div class="text-center p-3 bg-white rounded-lg border border-orange-200">
                <div class="text-lg font-bold" [ngClass]="{
                       'text-gray-600': getMasterRemainingAmount() === 0,
                       'text-orange-600': getMasterRemainingAmount() > 0,
                       'text-red-600': getMasterRemainingAmount() < 0
                     }">
                  {{ formatCurrency(getMasterRemainingAmount()) }}
                </div>
                <div class="text-xs text-gray-700">
                  {{ getMasterRemainingAmount() === 0 ? 'Distribución Completa' :
                  getMasterRemainingAmount() > 0 ? 'Restante por Asignar' :
                  'Exceso Asignado' }}
                </div>
              </div>
            </div>

            <!-- Barra de progreso -->
            <div class="mt-3">
              <div class="flex justify-between text-xs text-gray-600 mb-1">
                <span>Progreso: {{ masterPaymentPlans.length }} cuotas</span>
                <span>{{ getMasterProgressPercentage() }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div class="h-2 rounded-full transition-all duration-300" [ngClass]="{
                       'bg-green-500': getMasterProgressPercentage() <= 100,
                       'bg-red-500': getMasterProgressPercentage() > 100
                     }" [style.width.%]="Math.min(getMasterProgressPercentage(), 100)">
                </div>
              </div>
            </div>

            <!-- Información adicional del plan maestro -->
            <div class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div class="text-xs text-yellow-800">
                <div class="font-semibold mb-1">📋 Aplicación del Plan Maestro:</div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>• <span class="font-medium">{{ businessParticipants.length }} participantes</span> tendrán este
                    plan</div>
                  <div>• <span class="font-medium">{{ businessParticipants.length * masterPaymentPlans.length }}</span>
                    links de pago se generarán</div>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <div *ngFor="let plan of masterPaymentPlans; let i = index"
              class="bg-white border border-gray-200 rounded-lg p-4">
              <div class="flex items-center justify-between mb-3">
                <h6 class="font-semibold text-gray-800">Cuota #{{ plan.installmentNumber }} - Plan Maestro</h6>
                <button type="button" (click)="removeMasterPaymentPlan(i)"
                  class="text-red-600 hover:text-red-800 p-1 rounded transition-colors">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"></path>
                  </svg>
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">📅 Fecha de Vencimiento</label>
                  <p-calendar [(ngModel)]="plan.dueDate" [ngModelOptions]="{standalone: true}"
                    class="w-full tw-calendar" placeholder="Seleccionar fecha" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">💰 Monto a Pagar (por
                    participante)</label>
                  <input type="text" appCurrencyFormatter [(ngModel)]="plan.amountToPay"
                    [ngModelOptions]="{standalone: true}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">📝 Observaciones</label>
                  <input type="text" [(ngModel)]="plan.observations" [ngModelOptions]="{standalone: true}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Opcional" />
                </div>
              </div>
            </div>

            <!-- Mensaje cuando no hay cuotas -->
            <div *ngIf="masterPaymentPlans.length === 0"
              class="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <div class="text-gray-400 mb-2">
                <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                  </path>
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900">Sin plan maestro definido</h3>
              <p class="text-sm text-gray-500">Agregue al menos una cuota al plan maestro de participantes</p>
            </div>
          </div>
        </div>

        <!-- Información para modalidad Total -->
        <div *ngIf="selectedCoverageModality === CoverageModality.Total"
          class="border border-green-200 rounded-lg p-6 bg-green-50">
          <div class="flex items-center mb-4">
            <span class="text-2xl mr-3">🟢</span>
            <h5 class="text-lg font-semibold text-green-800">Modalidad A: Cobertura Total</h5>
          </div>
          <div class="text-sm text-green-700">
            <p class="mb-2">
              <span class="font-semibold">✅ Configuración completa:</span> La empresa pagará el 100% de todas las
              inscripciones.
            </p>
            <p>
              Los participantes no requerirán planes de pago individuales ya que no pagarán nada. Solo necesita
              configurar el plan de pagos de la empresa arriba.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 5: Resumen Final 🆕 -->
    <div *ngIf="businessStep === 5" class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div class="flex items-center mb-6">
        <div class="w-2 h-8 bg-green-500 rounded mr-3"></div>
        <h3 class="text-lg font-semibold text-gray-800">Resumen Final - Confirmación</h3>
      </div>

      <!-- Resumen completo del acuerdo -->
      <div class="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 mb-6">
        <div class="flex items-center justify-between mb-6">
          <h4 class="text-xl font-bold text-gray-800">📋 Resumen del Acuerdo Corporativo</h4>
          <div class="flex space-x-2">
            <span class="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
              {{ selectedCoverageModality === CoverageModality.Total ? 'Modalidad A' : 'Modalidad B' }}
            </span>
            <span class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              {{ businessParticipants.length }} participantes
            </span>
          </div>
        </div>

        <!-- Información general -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div class="space-y-4">
            <div class="bg-white p-4 rounded-lg shadow-sm">
              <h5 class="font-semibold text-gray-800 mb-3">🏢 Información Empresarial</h5>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600">Empresa:</span>
                  <span class="font-semibold">{{ getSelectedCompanyName() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Proyecto:</span>
                  <span class="font-semibold">{{ selectedProject?.name }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Modalidad:</span>
                  <span class="font-semibold">{{ selectedCoverageModality === CoverageModality.Total ? 'Cobertura Total
                    (100%)' : 'Cobertura Parcial' }}</span>
                </div>
                <!-- 🌍 Información de moneda para inscripciones internacionales -->
                <div *ngIf="businessIsInternational" class="flex justify-between">
                  <span class="text-gray-600">Tipo:</span>
                  <span class="font-semibold text-blue-600">🌐 Internacional ({{ businessCurrencyCode }})</span>
                </div>
                <div *ngIf="businessIsInternational && businessExchangeRate > 0" class="flex justify-between">
                  <span class="text-gray-600">Tasa de cambio:</span>
                  <span class="font-semibold text-blue-600">{{ formatBusinessExchangeRate() }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <div class="bg-white p-4 rounded-lg shadow-sm">
              <h5 class="font-semibold text-gray-800 mb-3">💰 Plan de Pagos</h5>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600">Cuotas empresa:</span>
                  <span class="font-semibold">{{ companyPaymentPlans.length }} cuotas</span>
                </div>
                <div class="flex justify-between" *ngIf="selectedCoverageModality === CoverageModality.Partial">
                  <span class="text-gray-600">Plan maestro:</span>
                  <span class="font-semibold">{{ masterPaymentPlans.length }} cuotas</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Monto inscripción:</span>
                  <span class="font-semibold">{{ getBusinessCurrencySymbol() }}{{
                    formatCurrency(businessInscriptionAmount) }}</span>
                </div>
                <!-- 🌍 Equivalente en COP para inscripciones internacionales -->
                <div *ngIf="businessIsInternational && businessExchangeRate > 0"
                  class="flex justify-between text-blue-600">
                  <span>Equivalente COP:</span>
                  <span class="font-semibold">{{ calculateBusinessCOPEquivalent(businessInscriptionAmount) |
                    number:'1.0-0' }} COP</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Cálculos financieros finales -->
        <div class="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
          <h5 class="font-bold text-gray-800 mb-4 flex items-center">
            <span class="text-2xl mr-2">💎</span>
            Resumen Financiero
            <span *ngIf="businessIsInternational" class="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
              En {{ businessCurrencyCode }}
            </span>
          </h5>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div class="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div class="text-2xl font-bold text-blue-600">{{ businessParticipants.length }}</div>
              <div class="text-sm text-blue-700 font-medium">Participantes</div>
            </div>

            <div class="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div class="text-lg font-bold text-purple-600">{{ getBusinessCurrencySymbol() }}{{
                formatCurrency(totalEnrollmentAmount) }}</div>
              <div class="text-sm text-purple-700 font-medium">Total del Programa</div>
              <div *ngIf="businessIsInternational && businessExchangeRate > 0" class="text-xs text-purple-500 mt-1">
                ≈ {{ calculateBusinessCOPEquivalent(totalEnrollmentAmount) | number:'1.0-0' }} COP
              </div>
            </div>

            <div class="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div class="text-lg font-bold text-green-600">{{ getBusinessCurrencySymbol() }}{{
                formatCurrency(totalCompanyAmount) }}</div>
              <div class="text-sm text-green-700 font-medium">Empresa Paga</div>
              <div class="text-xs text-green-600 mt-1">
                {{ ((totalCompanyAmount / totalEnrollmentAmount) * 100).toFixed(1) }}%
              </div>
            </div>

            <div class="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div class="text-lg font-bold text-orange-600">{{ getBusinessCurrencySymbol() }}{{
                formatCurrency(totalParticipantAmount) }}</div>
              <div class="text-sm text-orange-700 font-medium">Participantes Pagan</div>
              <div class="text-xs text-orange-600 mt-1">
                {{ ((totalParticipantAmount / totalEnrollmentAmount) * 100).toFixed(1) }}%
              </div>
            </div>
          </div>
        </div>

        <!-- Información sobre links de pago -->
        <div class="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h6 class="font-semibold text-blue-800 mb-2">🔗 Links de Pago que se Generarán:</h6>
          <div class="text-sm text-blue-700">
            <div *ngIf="selectedCoverageModality === CoverageModality.Total">
              • <span class="font-semibold">{{ companyPaymentPlans.length }} links para la empresa</span> ({{
              formatCurrency(companyPaymentPlans.length > 0 ? totalCompanyAmount / companyPaymentPlans.length : 0) }}
              por cuota)
            </div>
            <div *ngIf="selectedCoverageModality === CoverageModality.Partial">
              • <span class="font-semibold">{{ companyPaymentPlans.length }} links para la empresa</span> ({{
              formatCurrency(companyPaymentPlans.length > 0 ? totalCompanyAmount / companyPaymentPlans.length : 0) }}
              por cuota)<br>
              • <span class="font-semibold">{{ businessParticipants.length * masterPaymentPlans.length }} links para
                participantes</span> ({{ businessParticipants.length }} participantes × {{ masterPaymentPlans.length }}
              cuotas)
            </div>
          </div>
        </div>

        <!-- 📧 Información sobre envío de links de pago -->
        <div class="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
          <h6 class="font-semibold text-indigo-800 mb-2">📧 Links de Pago que se Enviarán por Email:</h6>
          <div class="text-sm text-indigo-700 space-y-2">
            <div class="bg-white p-3 rounded-lg border border-indigo-100">
              <div class="flex items-center justify-between mb-2">
                <span class="font-semibold text-indigo-800">🏢 Empresa</span>
                <span class="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs">{{ companyPaymentPlans.length }}
                  emails</span>
              </div>
              <div class="text-xs text-indigo-600">
                • Se enviará 1 email por cada cuota de la empresa<br>
                • Fecha de envío: <span class="font-medium">10 días antes del vencimiento</span><br>
                • Los links tendrán vigencia de <span class="font-medium">10 días adicionales</span> después del
                vencimiento
              </div>
            </div>

            <div *ngIf="selectedCoverageModality === CoverageModality.Partial"
              class="bg-white p-3 rounded-lg border border-indigo-100">
              <div class="flex items-center justify-between mb-2">
                <span class="font-semibold text-indigo-800">👥 Participantes</span>
                <span class="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs">{{ businessParticipants.length *
                  masterPaymentPlans.length }} emails</span>
              </div>
              <div class="text-xs text-indigo-600">
                • Se enviará 1 email por cuota a cada participante<br>
                • Fecha de envío: <span class="font-medium">10 días antes del vencimiento</span><br>
                • Los links tendrán vigencia de <span class="font-medium">10 días adicionales</span> después del
                vencimiento
              </div>
            </div>

            <div class="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mt-3">
              <div class="flex items-start">
                <svg class="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"></path>
                </svg>
                <div class="text-xs text-yellow-800">
                  <span class="font-semibold">Importante:</span> Los emails se enviarán automáticamente según las fechas
                  programadas.
                  Asegúrese de que todas las direcciones de email estén correctas antes de proceder.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Observaciones si las hay -->
        <div *ngIf="businessObservations" class="mt-6 p-4 bg-gray-50 rounded-lg">
          <h6 class="font-semibold text-gray-800 mb-2">📝 Observaciones:</h6>
          <p class="text-sm text-gray-700">{{ businessObservations }}</p>
        </div>

        <!-- Confirmación final -->
        <div class="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div class="flex items-start">
            <svg class="w-6 h-6 text-yellow-600 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"></path>
            </svg>
            <div>
              <h6 class="font-semibold text-yellow-800 mb-1">⚠️ Confirmación Requerida</h6>
              <p class="text-sm text-yellow-700">
                Al hacer clic en "Crear Inscripciones", se generará el acuerdo corporativo y se procesarán todas las
                inscripciones.
                Esta acción <span class="font-semibold">no se puede deshacer</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navegación del flujo empresarial -->
    <div class="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
      <!-- Botón Atrás -->
      <button *ngIf="businessStep > 1" type="button" (click)="goToBusinessStep(businessStep - 1)"
        class="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd"></path>
        </svg>
        Atrás
      </button>

      <!-- Espaciador cuando no hay botón atrás -->
      <div *ngIf="businessStep === 1"></div>

      <!-- Botón Siguiente (Steps 1, 2, 3, 4) -->
      <button *ngIf="businessStep < 5" type="button" [disabled]="!isBusinessStepValid(businessStep)"
        (click)="goToBusinessStep(businessStep + 1)"
        class="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2">
        {{ businessStep === 4 ? 'Ver Resumen' : 'Siguiente' }}
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd"></path>
        </svg>
      </button>

      <!-- Botón Crear Inscripciones (Step 5) -->
      <button *ngIf="businessStep === 5" type="button" [disabled]="!isBusinessStepValid(5)"
        (click)="submitBusinessEnrollment()"
        class="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-8 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 flex items-center gap-2 shadow-lg">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clip-rule="evenodd"></path>
        </svg>
        Crear Inscripciones Empresariales
      </button>
    </div>
  </div>

  <!-- 🔷 Barra de pasos (solo para flujo individual nacional/internacional) -->
  <div
    *ngIf="!isBlocked && enrollmentForm && selectedEnrollmentType && (selectedEnrollmentType === 'national' || selectedEnrollmentType === 'international')"
    class="flex items-center justify-center mb-8">
    <div class="flex w-full">
      <ng-container *ngFor="let label of ['Datos Básicos', 'Planes de Pago']; let i = index">
        <div class="flex w-1/2 text-center items-center">
          <div class="flex items-center justify-center w-10 h-10 rounded-full" [ngClass]="{
              'bg-blue-500 text-white': step === i + 1,
              'bg-gray-300 text-gray-600': step !== i + 1
            }">
            {{ i + 1 }}
          </div>
          <div class="mx-2">
            {{ label }}
          </div>
        </div>
      </ng-container>
    </div>
  </div>

  <!-- 🔶 Formulario (solo para flujo individual nacional/internacional) -->
  <form [formGroup]="enrollmentForm" (ngSubmit)="onSubmit()"
    *ngIf="!isBlocked && enrollmentForm && selectedEnrollmentType && (selectedEnrollmentType === 'national' || selectedEnrollmentType === 'international')">

    <!-- STEP 1 -->
    <div *ngIf="step === 1">

      <!-- Información específica para inscripciones internacionales -->
      <div *ngIf="selectedEnrollmentType === 'international'"
        class="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
        <div class="flex items-center mb-2">
          <span class="text-blue-600 font-semibold">🌍 Inscripción Internacional Seleccionada</span>
        </div>
        <div class="text-sm text-blue-700">
          <p class="mb-2">
            <strong>Importante:</strong> Para inscripciones internacionales se aplicarán tasas de cambio actualizadas.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>• Los montos se muestran convertidos a COP para el procesamiento</div>
            <div>• Se aplicará la tasa de cambio vigente del proyecto</div>
            <div>• Los pagos se procesan en pesos colombianos</div>
            <div>• Se mantendrá registro del monto original</div>
          </div>
        </div>
      </div>

      <!-- Información específica para inscripciones nacionales -->
      <div *ngIf="selectedEnrollmentType === 'national'"
        class="bg-green-50 p-4 rounded-lg border border-green-200 mb-6">
        <div class="flex items-center mb-2">
          <span class="text-green-600 font-semibold">🇨🇴 Inscripción Nacional Seleccionada</span>
        </div>
        <div class="text-sm text-green-700">
          <p>
            <strong>Proceso estándar:</strong> Todos los montos se manejan en pesos colombianos (COP).
            No se requiere conversión de moneda.
          </p>
        </div>
      </div>

      <!-- Datos básicos mejorados -->
      <div class="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
        <div class="flex items-center mb-4">
          <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
          <h3 class="text-lg font-semibold text-gray-800">Datos Básicos de la Inscripción</h3>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- ProjectId -->
          <div>
            <label for="projectId" class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>
              📁 Proyecto
            </label>
            <p-dropdown id="projectId" formControlName="projectId" [filter]="true" filterBy="label" [showClear]="true"
              [options]="projects" placeholder="Seleccione un proyecto" (onFilter)="onSearchProjects($event)"
              class="w-full custom-dropdown">
            </p-dropdown>
            <div *ngIf="enrollmentForm.get('projectId')?.invalid && enrollmentForm.get('projectId')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"></path>
              </svg>
              El campo Proyecto es obligatorio.
            </div>
            <p *ngIf="!enrollmentForm.get('projectId')?.invalid" class="text-xs text-gray-500 mt-1">
              Seleccione el proyecto en el que se inscribirá el estudiante
            </p>
          </div>

          <!-- ParticipantId -->
          <div>
            <label for="participantId" class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>
              👤 Participante
            </label>
            <p-dropdown id="participantId" formControlName="participantId" [filter]="true" filterBy="label"
              [showClear]="true" resetFilterOnHide="true" [loading]="isSearchingParticipants" [options]="participants"
              placeholder="Seleccione un participante" (onFilter)="onSearchParticipants($event)"
              class="w-full custom-dropdown">
            </p-dropdown>
            <div *ngIf="enrollmentForm.get('participantId')?.invalid && enrollmentForm.get('participantId')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"></path>
              </svg>
              El campo Participante es obligatorio.
            </div>
            <p *ngIf="!enrollmentForm.get('participantId')?.invalid" class="text-xs text-gray-500 mt-1">
              Estudiante que será inscrito en el proyecto
            </p>
          </div>

          <!-- Fecha -->
          <div>
            <label for="dateEnrolled" class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>
              📅 Fecha de Inscripción
            </label>
            <p-calendar formControlName="dateEnrolled" class="w-full tw-calendar" placeholder="Seleccionar fecha" />
            <div *ngIf="enrollmentForm.get('dateEnrolled')?.invalid && enrollmentForm.get('dateEnrolled')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"></path>
              </svg>
              El campo Fecha de Inscripción es obligatorio.
            </div>
            <p *ngIf="!enrollmentForm.get('dateEnrolled')?.invalid" class="text-xs text-gray-500 mt-1">
              Fecha en la que se realiza la inscripción
            </p>
          </div>

          <!-- Total -->
          <div>
            <label for="totalAmount" class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>
              💰 Monto Total de Inscripción
              <span *ngIf="selectedEnrollmentType === 'national'">(COP)</span>
              <span *ngIf="selectedEnrollmentType === 'international'">({{ getCurrentCurrencyCode() }})</span>
              <span *ngIf="!selectedEnrollmentType">(COP)</span>
            </label>
            <input type="text" appCurrencyFormatter id="totalAmount" formControlName="totalAmount"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autocomplete="off"
              [placeholder]="selectedEnrollmentType === 'international' ? getCurrentCurrencySymbol() + '0' : '$0'">
            <div *ngIf="enrollmentForm.get('totalAmount')?.invalid && enrollmentForm.get('totalAmount')?.touched"
              class="text-red-500 text-sm mt-1 flex items-center">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"></path>
              </svg>
              El monto total es obligatorio.
            </div>
            <p *ngIf="!enrollmentForm.get('totalAmount')?.invalid" class="text-xs text-gray-500 mt-1">
              <span *ngIf="selectedEnrollmentType === 'national'">Monto total en pesos colombianos que pagará el
                estudiante</span>
              <span *ngIf="selectedEnrollmentType === 'international'">Monto total en {{ getCurrentCurrencyCode() }} que
                pagará el estudiante</span>
              <span *ngIf="!selectedEnrollmentType">Monto total que pagará el estudiante</span>
            </p>

            <!-- Mostrar equivalencia para inscripciones internacionales -->
            <div
              *ngIf="selectedEnrollmentType === 'international' && enrollmentForm.get('totalAmount')?.value && currentExchangeRate > 0"
              class="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
              <div class="flex items-center justify-between">
                <span class="text-blue-700">Equivalente en COP:</span>
                <span class="font-semibold text-blue-800">
                  {{ (enrollmentForm.get('totalAmount')?.value * currentExchangeRate) | currency:'COP':'symbol':'1.0-0'
                  }}
                </span>
              </div>
              <div class="text-xs text-blue-600 mt-1">
                Tasa: {{ formatExchangeRate() }}
              </div>
            </div>
          </div>

          <!-- Monto sugerido -->
          <div class="md:col-span-2">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              🏷️
              <span *ngIf="selectedEnrollmentType === 'national'">Monto de Inscripción Sugerido (COP)</span>
              <span *ngIf="selectedEnrollmentType === 'international'">Monto de Inscripción Sugerido ({{
                projectCurrency?.code || 'USD' }})</span>
              <span *ngIf="!selectedEnrollmentType">Monto de Inscripción Sugerido</span>
            </label>
            <input type="text" appCurrencyFormatter formControlName="inscriptionAmount" readonly
              class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">

            <!-- Información de proyecto internacional -->
            <div *ngIf="isInternationalProject && projectCurrency"
              class="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div class="flex items-center mb-2">
                <span class="text-blue-600 font-semibold">🌍 Proyecto Internacional</span>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <span class="text-gray-600">Moneda Original:</span>
                  <span class="font-semibold ml-1">{{ projectCurrency.name }} ({{ projectCurrency.code }})</span>
                </div>

                <div>
                  <span class="text-gray-600">Monto Original:</span>
                  <span class="font-semibold ml-1">
                    {{ selectedProject?.inscriptionAmountUSD | currency:projectCurrency.code:'symbol':'1.2-2' }}
                  </span>
                </div>

                <div>
                  <span class="text-gray-600">Tasa de Cambio:</span>
                  <span class="font-semibold ml-1">{{ formatExchangeRate() }}</span>
                </div>

                <div *ngIf="lastExchangeRateUpdate">
                  <span class="text-gray-600">Última Actualización:</span>
                  <span class="font-semibold ml-1">{{ lastExchangeRateUpdate | date:'short' }}</span>
                </div>
              </div>

              <div class="mt-2 text-xs text-blue-600">
                💡 El monto mostrado arriba es la conversión automática a COP para el procesamiento de pagos
              </div>
            </div>

            <span class="text-sm text-gray-500" *ngIf="selectedEnrollmentType === 'national'">
              Este es el monto de inscripción configurado para el proyecto en pesos colombianos
            </span>
            <span class="text-sm text-gray-500"
              *ngIf="selectedEnrollmentType === 'international' && !isInternationalProject">
              Para inscripciones internacionales, este monto se mostrará en la moneda del proyecto
            </span>
          </div>

          <!-- Campos ocultos para inscripciones empresariales ya que se manejan automáticamente -->

          <!-- ❌ CAMPO REMOVIDO: isCompany (obsoleto tras separación individual/corporativo) -->

          <!-- ❌ CAMPO REMOVIDO: whoPays (obsoleto tras separación individual/corporativo) -->

          <!-- Observaciones -->
          <div class="md:col-span-2">
            <label for="observations" class="block text-sm font-semibold text-gray-700 mb-2">
              📝 Observaciones
            </label>
            <textarea id="observations" formControlName="observations"
              class="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3" placeholder="Observaciones adicionales sobre la inscripción (opcional)"></textarea>
          </div>

          <!-- Estado Inicial -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2" appRequiredLabel>
              📊 Estado Inicial
            </label>
            <p-dropdown [options]="statusOptions" formControlName="status" optionLabel="label" optionValue="value"
              styleClass="w-full" placeholder="Seleccione el estado">
            </p-dropdown>
            <p class="text-xs text-gray-500 mt-1">
              Estado inicial de la inscripción al crearla
            </p>
          </div>

          <!-- 🆕 Cobro automático vía email -->
          <div class="md:col-span-2">
            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <label class="block text-sm font-semibold text-gray-700 mb-1">📧 Cobro automático vía email</label>
                  <p class="text-xs text-gray-600">
                    Si está activo, el sistema generará links de pago automáticamente y los enviará por email 2 días
                    antes del vencimiento de cada cuota.
                  </p>
                </div>
                <div class="ml-4">
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" formControlName="autoGeneratePaymentLinks" class="sr-only peer">
                    <div
                      class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600">
                    </div>
                  </label>
                </div>
              </div>
              <div class="mt-2 text-xs" [ngClass]="{
                'text-green-600': enrollmentForm.get('autoGeneratePaymentLinks')?.value,
                'text-orange-600': !enrollmentForm.get('autoGeneratePaymentLinks')?.value
              }">
                <span *ngIf="enrollmentForm.get('autoGeneratePaymentLinks')?.value">
                  ✅ El participante recibirá links de pago automáticamente por email.
                </span>
                <span *ngIf="!enrollmentForm.get('autoGeneratePaymentLinks')?.value">
                  ⚠️ No se generarán links automáticos. Los pagos se gestionarán manualmente.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 2: Planes de pago -->
    <div *ngIf="step === 2" formArrayName="paymentPlans">

      <!-- Resumen mejorado con mejor diseño -->
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-6 mb-6 rounded-lg shadow-sm">
        <div class="flex items-center mb-4">
          <div class="w-2 h-8 bg-blue-500 rounded mr-3"></div>
          <h3 class="text-lg font-semibold text-gray-800">Resumen del Plan de Pagos</h3>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Total de Inscripción -->
          <div class="bg-white p-4 rounded-lg border border-blue-100">
            <div class="flex items-center mb-2">
              <span class="text-2xl mr-2">💰</span>
              <span class="text-sm font-medium text-gray-600">Total de Inscripción</span>
            </div>
            <!-- Mostrar en la moneda principal según el tipo de inscripción -->
            <p class="text-2xl font-bold text-gray-800">
              {{ enrollmentForm.get('totalAmount')?.value | currency:getCurrentCurrencyCode():'symbol':'1.0-0' }}
            </p>

            <!-- Equivalente en COP para proyectos internacionales -->
            <div
              *ngIf="selectedEnrollmentType === 'international' && enrollmentForm.get('totalAmount')?.value && currentExchangeRate > 0"
              class="mt-2">
              <p class="text-sm text-gray-600 font-medium">
                ≈ {{ (enrollmentForm.get('totalAmount')?.value * currentExchangeRate) | currency:'COP':'symbol':'1.0-0'
                }}
              </p>
              <p class="text-xs text-gray-500">Equivalente en COP</p>
            </div>
          </div>

          <!-- Número de cuotas -->
          <div class="bg-white p-4 rounded-lg border border-blue-100">
            <div class="flex items-center mb-2">
              <span class="text-2xl mr-2">📅</span>
              <span class="text-sm font-medium text-gray-600">Número de Cuotas</span>
            </div>
            <p class="text-2xl font-bold text-gray-800">{{ paymentPlans.length }}</p>
            <p class="text-xs text-gray-500 mt-1">Planes de pago configurados</p>
          </div>

          <!-- Total asignado -->
          <div class="bg-white p-4 rounded-lg border border-blue-100">
            <div class="flex items-center mb-2">
              <span class="text-2xl mr-2">🧮</span>
              <span class="text-sm font-medium text-gray-600">Total Asignado</span>
            </div>
            <p class="text-2xl font-bold" [ngClass]="{
                 'text-green-600': totalAssignedAmount === enrollmentForm.get('totalAmount')?.value,
                 'text-red-600': totalAssignedAmount !== enrollmentForm.get('totalAmount')?.value,
                 'text-gray-800': totalAssignedAmount === 0
               }">
              {{ totalAssignedAmount | currency:getCurrentCurrencyCode():'symbol':'1.0-0' }}
            </p>

            <!-- Equivalente en COP para proyectos internacionales -->
            <div
              *ngIf="selectedEnrollmentType === 'international' && totalAssignedAmount > 0 && currentExchangeRate > 0"
              class="mt-2">
              <p class="text-sm text-gray-600 font-medium">
                ≈ {{ (totalAssignedAmount * currentExchangeRate) | currency:'COP':'symbol':'1.0-0' }}
              </p>
              <p class="text-xs text-gray-500">Equivalente en COP</p>
            </div>

            <!-- Indicador de estado -->
            <div class="mt-2">
              <span *ngIf="isTotalAmountWithinTolerance()"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                ✓ Completo
              </span>
              <span
                *ngIf="!isTotalAmountWithinTolerance() && totalAssignedAmount < enrollmentForm.get('totalAmount')?.value"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                ⚠ Faltante
              </span>
              <span
                *ngIf="!isTotalAmountWithinTolerance() && totalAssignedAmount > enrollmentForm.get('totalAmount')?.value"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                ⚠ Excedente
              </span>
            </div>
          </div>
        </div>

        <!-- Información adicional para proyectos internacionales -->
        <div *ngIf="selectedEnrollmentType === 'international' && projectCurrency"
          class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div class="flex items-center mb-3">
            <span class="text-blue-600 text-lg mr-2">🌍</span>
            <span class="font-semibold text-blue-800">Información de Proyecto Internacional</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Moneda del Proyecto:</span>
              <span class="font-semibold text-gray-800">{{ projectCurrency.name }} ({{ projectCurrency.code }})</span>
            </div>

            <div class="flex justify-between">
              <span class="text-gray-600">Tasa de Cambio:</span>
              <span class="font-semibold text-gray-800">{{ formatExchangeRate() }}</span>
            </div>

            <div *ngIf="lastExchangeRateUpdate" class="flex justify-between">
              <span class="text-gray-600">Última Actualización:</span>
              <span class="font-semibold text-gray-800">{{ lastExchangeRateUpdate | date:'short' }}</span>
            </div>
          </div>

          <div class="mt-3 p-3 bg-blue-100 rounded border border-blue-200">
            <p class="text-xs text-blue-700 font-medium">
              💡 <strong>Importante:</strong> Los pagos se procesan en pesos colombianos (COP).
              Los montos en {{ projectCurrency.code }} son informativos y se calculan con la tasa de cambio actual.
            </p>
          </div>
        </div>
      </div>

      <!-- ❗Validación cuando la suma está fuera de tolerancia -->
      <div *ngIf="hasSignificantTotalError()"
        class="text-red-600 bg-red-100 border border-red-300 px-4 py-2 rounded mb-4">
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"></path>
          </svg>
          <div>
            <strong>⚠️ Diferencia significativa detectada:</strong>
            <div class="mt-1">
              Total asignado: <strong>{{ totalAssignedAmount | currency:getCurrentCurrencyCode() }}</strong> |
              Total requerido: <strong>{{ enrollmentForm.get('totalAmount')?.value | currency:getCurrentCurrencyCode()
                }}</strong>
              <span *ngIf="totalAssignedAmount < enrollmentForm.get('totalAmount')?.value" class="ml-2">
                (Faltan {{ (enrollmentForm.get('totalAmount')?.value - totalAssignedAmount) |
                currency:getCurrentCurrencyCode() }})
              </span>
              <span *ngIf="totalAssignedAmount > enrollmentForm.get('totalAmount')?.value" class="ml-2">
                (Exceden {{ (totalAssignedAmount - enrollmentForm.get('totalAmount')?.value) |
                currency:getCurrentCurrencyCode() }})
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Planes de pago mejorados -->
      <div *ngFor="let plan of paymentPlans.controls; let i = index" [formGroupName]="i"
        class="bg-white border border-gray-200 rounded-lg p-6 mb-4 shadow-sm hover:shadow-md transition-shadow">

        <!-- Header de la cuota -->
        <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
          <div class="flex items-center">
            <div
              class="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
              {{ i + 1 }}
            </div>
            <div>
              <h4 class="font-semibold text-gray-800">Cuota #{{ i + 1 }}</h4>
              <p class="text-xs text-gray-500">Plan de pago</p>
            </div>
          </div>

          <button type="button"
            class="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-full transition-colors"
            (click)="removePaymentPlan(i)" [disabled]="paymentPlans.length === 1">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"></path>
            </svg>
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Fecha de Vencimiento -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              📅 Fecha de Vencimiento
            </label>
            <p-calendar formControlName="dueDate" class="w-full tw-calendar" placeholder="Seleccionar fecha" />
            <p class="text-xs text-gray-500 mt-1">Fecha límite de pago</p>
          </div>

          <!-- Valor a Pagar -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              💰 Valor a Pagar ({{ getCurrentCurrencyCode() }})
            </label>
            <input type="text" formControlName="amountToPay" appCurrencyFormatter
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              [placeholder]="getCurrentCurrencySymbol() + '0'" />

            <!-- Mostrar equivalente en COP para proyectos internacionales -->
            <div
              *ngIf="selectedEnrollmentType === 'international' && plan.get('amountToPay')?.value && currentExchangeRate > 0"
              class="mt-1 text-xs text-gray-600">
              ≈ {{ (plan.get('amountToPay')?.value * currentExchangeRate) | currency:'COP':'symbol':'1.0-0' }} COP
            </div>
          </div>

          <!-- Estado del Pago -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              📊 Estado del Pago
            </label>
            <select formControlName="paymentStatus"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="LINK_GENERATED">Link Generado</option>
              <option value="PENDING">Pendiente</option>
              <option value="PAID">Pagado</option>
              <option value="CANCELED">Cancelado</option>
            </select>
          </div>

          <!-- 🆕 Generar Link Automático -->
          <div class="flex items-center">
            <input type="checkbox" formControlName="generateLink"
              class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <label class="ml-2 text-sm text-gray-700">
              🔗 Generar link de pago automático
            </label>
          </div>

          <!-- Observaciones -->
          <div class="md:col-span-2">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              📝 Observaciones
            </label>
            <textarea formControlName="observations"
              class="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2" placeholder="Observaciones adicionales (opcional)"></textarea>
          </div>
        </div>
      </div>

      <!-- 🧮 NUEVA SECCIÓN: BOTONES DE GESTIÓN DE CUOTAS -->
      <div class="mt-6 space-y-3">

        <!-- Botón de cálculo automático -->
        <button type="button" (click)="openCalculateModal()"
          class="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold px-6 py-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-md hover:shadow-lg">
          <div class="flex items-center justify-center gap-3">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z">
              </path>
            </svg>
            <span>✨ Calcular Cuotas Automáticamente</span>
          </div>
          <p class="text-xs mt-1 opacity-75">
            Distribuye el monto restante en cuotas iguales con fechas automáticas
          </p>
        </button>

        <!-- Botón manual para agregar plan de pago -->
        <button type="button" (click)="addPaymentPlan()" [disabled]="exceedsTotalAmount"
          class="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 disabled:from-gray-300 disabled:to-gray-300 text-white font-semibold px-6 py-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed shadow-md hover:shadow-lg disabled:shadow-none">
          <div class="flex items-center justify-center gap-3">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clip-rule="evenodd"></path>
            </svg>
            <span>📝 Agregar Cuota Individual</span>
          </div>
          <p *ngIf="!exceedsTotalAmount" class="text-xs mt-1 opacity-75">
            Agregar una sola cuota manualmente con valores personalizados
          </p>
        </button>

        <!-- 🔒 Mensajes de límite alcanzado -->
        <div *ngIf="exceedsTotalAmount" class="mt-2 text-center">
          <p class="text-sm text-red-600">⚠️ No se pueden agregar más cuotas. El total ya está asignado.</p>
        </div>
      </div>
    </div>

    <!-- BOTONES DE NAVEGACIÓN MEJORADOS -->
    <div class="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">

      <!-- Botón Atrás -->
      <button *ngIf="step > 1" type="button" (click)="goToStep(step - 1)"
        class="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd"></path>
        </svg>
        Atrás
      </button>

      <!-- Espaciador cuando no hay botón atrás -->
      <div *ngIf="step === 1"></div>

      <!-- Botón Siguiente (Step 1) -->
      <button *ngIf="step === 1" type="button" [disabled]="!isCurrentStepValid()"
        (click)="isCurrentStepValid() ? goToStep(2) : logInvalidControls()"
        class="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2">
        Siguiente
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd"></path>
        </svg>
      </button>

      <!-- Botón Guardar (Step 2) -->
      <button *ngIf="step === 2" type="submit"
        [disabled]="!isCurrentStepValid() || enrollmentForm.invalid || exceedsTotalAmount"
        class="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-8 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 flex items-center gap-2 shadow-lg">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clip-rule="evenodd"></path>
        </svg>
        Guardar Inscripción
      </button>
    </div>
  </form>
</div>

<!-- 🧮 MODAL DE CÁLCULO AUTOMÁTICO DE CUOTAS -->
<div *ngIf="showCalculateModal" class="fixed inset-0 z-50 overflow-y-auto">
  <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
    <!-- Overlay -->
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" (click)="closeCalculateModal()"></div>

    <!-- Modal -->
    <div
      class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
      <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div class="sm:flex sm:items-start">
          <div
            class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
            <!-- Ícono de calculadora -->
            <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z">
              </path>
            </svg>
          </div>
          <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
              Calcular Cuotas Automáticamente
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">
                El monto total de inscripción se distribuirá automáticamente en cuotas iguales con fechas mensuales.
              </p>

              <!-- Información del cálculo -->
              <div class="mt-4 bg-blue-50 p-3 rounded-md">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">Monto total:</span>
                  <span class="font-medium">{{ enrollmentForm.get('totalAmount')?.value |
                    currency:getCurrentCurrencyCode():'symbol':'1.0-0' }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">Monto de inscripción:</span>
                  <span class="font-medium">{{ enrollmentForm.get('inscriptionAmount')?.value |
                    currency:getCurrentCurrencyCode():'symbol':'1.0-0' }}</span>
                </div>
                <div class="flex justify-between text-sm font-semibold border-t border-blue-200 pt-2 mt-2">
                  <span class="text-blue-700">Monto a distribuir:</span>
                  <span class="text-blue-700">{{ enrollmentForm.get('totalAmount')?.value |
                    currency:getCurrentCurrencyCode():'symbol':'1.0-0' }}</span>
                </div>
              </div>

              <!-- Selector de número de cuotas -->
              <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Número de cuotas:
                </label>
                <input type="range" [(ngModel)]="autoCalculateInstallments" [min]="minInstallments"
                  [max]="maxInstallments" step="1"
                  class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
                <div class="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{{ minInstallments }}</span>
                  <span class="font-bold text-blue-600">{{ autoCalculateInstallments }} cuotas</span>
                  <span>{{ maxInstallments }}</span>
                </div>

                <!-- Vista previa del monto por cuota -->
                <div class="mt-2 text-center p-2 bg-gray-50 rounded">
                  <span class="text-sm text-gray-600">Aproximadamente </span>
                  <span class="font-bold text-gray-800">
                    {{ (enrollmentForm.get('totalAmount')?.value / autoCalculateInstallments) |
                    currency:getCurrentCurrencyCode():'symbol':'1.0-0' }}
                  </span>
                  <span class="text-sm text-gray-600"> por cuota</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
        <button type="button" (click)="confirmAutoCalculate()"
          class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm">
          Generar Cuotas
        </button>
        <button type="button" (click)="closeCalculateModal()"
          class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
          Cancelar
        </button>
      </div>
    </div>
  </div>
</div>

<!-- 🚨 MODAL DE ALERTA DE LINKS DE PAGO AUTOMÁTICO -->
<div *ngIf="showPaymentLinkWarningModal" class="fixed inset-0 z-50 overflow-y-auto">
  <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
    <!-- Overlay oscuro -->
    <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" (click)="cancelPaymentLinkWarning()"></div>

    <!-- Modal -->
    <div
      class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border-4 border-orange-400">

      <!-- Header con alerta -->
      <div class="bg-gradient-to-r from-orange-500 to-yellow-500 px-6 py-4">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg class="h-8 w-8 text-white animate-pulse" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"></path>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-xl font-bold text-white">⚠️ Atención: Links de Pago Automático</h3>
          </div>
        </div>
      </div>

      <!-- Contenido -->
      <div class="bg-white px-6 py-6">
        <div class="text-center">
          <div class="bg-orange-100 border-2 border-orange-300 rounded-lg p-4 mb-4">
            <p class="text-lg font-semibold text-orange-800">
              Se detectaron <span class="text-2xl font-bold text-orange-600">{{ countAutoPaymentLinks() }}</span>
              cuota(s) con link de pago automático activado
            </p>
          </div>

          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p class="text-orange-700 font-medium mb-2">🔗 ¿Qué significa esto?</p>
            <ul class="text-sm text-gray-700 text-left list-disc list-inside space-y-1">
              <li>Se generarán <strong>links de pago automáticamente</strong> para las cuotas marcadas</li>
              <li>Los links serán enviados por <strong>correo electrónico</strong> al participante</li>
              <li>Los pagos quedarán <strong>pendientes de cobro</strong> hasta que el usuario pague</li>
            </ul>
          </div>

          <div class="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <p class="text-red-600 font-bold text-lg">
              ⚠️ ¿Está seguro de continuar?
            </p>
            <p class="text-red-500 text-sm mt-2">
              Esta acción generará links de pago que serán enviados automáticamente.
              <br>
              <strong>No se puede deshacer fácilmente.</strong>
            </p>
          </div>
        </div>
      </div>

      <!-- Botones de acción -->
      <div class="bg-gray-100 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button type="button" (click)="cancelPaymentLinkWarning()"
          class="w-full sm:w-auto px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center gap-2">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"></path>
          </svg>
          Cancelar y Revisar
        </button>
        <button type="button" (click)="confirmPaymentLinkWarning()"
          class="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"></path>
          </svg>
          Sí, Continuar con Links
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Modal de Confirmación -->
<app-confirmation-modal #confirmationModal [isVisible]="showConfirmationModal" [config]="confirmationConfig"
  (confirmed)="onConfirmationModalConfirmed()" (cancelled)="onConfirmationModalCancelled()"
  (closed)="onConfirmationModalClosed()">
</app-confirmation-modal>